/**
 * Character Count Tool
 *
 * Dedicated character counter with breakdown by type: letters, digits, spaces, special, unicode.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const CharacterCountTool: ITransformerTool = {
  id: 'character-count',
  name: 'Character Count',
  description: 'Count characters with breakdown: letters, digits, spaces, special chars, unicode',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['character', 'count', 'letter', 'digit', 'unicode', 'text', 'breakdown'],

  seo: {
    slug: 'character-counter',
    pageTitle: 'Character Counter - Free Online Tool | Prism',
    metaDescription: 'Count characters with detailed breakdown by type: letters, digits, spaces, special characters, and unicode. Fast, private, runs in your browser.',
    keywords: ['character counter', 'char count online', 'letter counter', 'digit counter', 'unicode character count'],
    relatedTools: ['word-count', 'line-count', 'sort-lines'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const text = input || '';

    let letters = 0;
    let uppercase = 0;
    let lowercase = 0;
    let digits = 0;
    let spaces = 0;
    let newlines = 0;
    let special = 0;
    let nonAscii = 0;

    for (const char of text) {
      const code = char.codePointAt(0) ?? 0;
      if (char >= 'A' && char <= 'Z') {
        letters++;
        uppercase++;
      } else if (char >= 'a' && char <= 'z') {
        letters++;
        lowercase++;
      } else if (char >= '0' && char <= '9') {
        digits++;
      } else if (char === '\n') {
        newlines++;
      } else if (/\s/.test(char)) {
        spaces++;
      } else if (code > 127) {
        nonAscii++;
      } else {
        special++;
      }
    }

    const total = text.length;

    return [
      `Total Characters:   ${total.toLocaleString()}`,
      ``,
      `Letters:            ${letters.toLocaleString()}`,
      `  Uppercase:        ${uppercase.toLocaleString()}`,
      `  Lowercase:        ${lowercase.toLocaleString()}`,
      `Digits:             ${digits.toLocaleString()}`,
      `Spaces:             ${spaces.toLocaleString()}`,
      `Newlines:           ${newlines.toLocaleString()}`,
      `Special Characters: ${special.toLocaleString()}`,
      `Non-ASCII/Unicode:  ${nonAscii.toLocaleString()}`,
    ].join('\n');
  },
};
